<template>
  <div
    class="pricing-block"
    role="region"
    :aria-label="`${message.heading} pricing information`"
  >
    <UCard
      class="pricing-card !bg-white !text-neutral-900 border border-neutral-200"
      :ui="{ body: { padding: 'p-6 md:p-8' } }"
    >
      <!-- Heading -->
      <template #header>
        <h3
          id="pricing-heading"
          class="text-2xl font-bold text-neutral-900"
        >
          {{ message.heading }}
        </h3>
      </template>

      <!-- Description -->
      <p
        id="pricing-description"
        class="text-base text-neutral-600 mb-6"
      >
        {{ message.description }}
      </p>

      <!-- Takeaways List -->
      <div
        v-if="message.takeaways && message.takeaways.length > 0"
        class="mb-8"
      >
        <ul
          class="space-y-3"
          role="list"
          aria-labelledby="pricing-heading"
        >
          <li
            v-for="(takeaway, index) in message.takeaways"
            :key="index"
            class="flex items-start gap-3"
            role="listitem"
          >
            <UIcon
              :name="takeaway.icon || 'i-heroicons-check-circle'"
              class="w-6 h-6 text-spruce-700 flex-shrink-0 mt-0.5"
              :class="{ 'text-neutral-400': !takeaway.icon }"
              aria-hidden="true"
            />
            <span class="text-neutral-700">
              {{ takeaway.text }}
            </span>
          </li>
        </ul>
      </div>

      <!-- Empty State (if no takeaways) -->
      <div
        v-else
        class="mb-8 p-4 bg-neutral-50 rounded text-neutral-500"
        role="status"
        aria-live="polite"
      >
        <p class="text-sm">
          No takeaways available
        </p>
      </div>

      <!-- CTA Button (conditional) -->
      <template
        v-if="message.cta"
        #footer
      >
        <UButton
          :href="message.cta.link"
          :external="message.cta.external"
          :ui="{ base: 'w-full focus:ring-2 focus:ring-offset-2 focus:ring-primary-500' }"
          size="md"
          color="primary"
          :target="message.cta.external ? '_blank' : undefined"
          :rel="message.cta.external ? 'noopener noreferrer' : undefined"
          :aria-label="message.cta.external ? `${message.cta.text} - Opens in new window` : message.cta.text"
          class="pricing-cta-button"
          @click="onCTAClick"
          @keydown.enter="onCTAClick"
          @keydown.space="onCTAClick"
        >
          {{ message.cta.text }}
        </UButton>
      </template>
    </UCard>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import type { PricingMessage } from '~/types/pricing'
import { useAnalytics } from '../../composables/useAnalytics'

interface Props {
  message: PricingMessage
  product?: string
}

const props = withDefaults(defineProps<Props>(), {
  product: 'Unknown'
})

// Initialize analytics
const { emitPricingBlockRendered, emitPricingCTAClicked } = useAnalytics()

/**
 * Emit analytics event when component mounts
 */
onMounted(() => {
  emitPricingBlockRendered(
    props.product,
    props.message.takeaways?.length || 0,
    !!props.message.cta
  )
})

/**
 * Handle CTA button click with analytics
 */
function onCTAClick(): void {
  if (props.message.cta) {
    emitPricingCTAClicked(
      props.message.cta.text,
      props.message.cta.link,
      props.message.cta.external ? 'external' : 'internal',
      props.product
    )
  }
}
</script>

<style scoped>
.pricing-block {
  width: 100%;
}

.pricing-card {
  transition: all 0.3s ease;
}

.pricing-card:hover {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

.pricing-cta-button {
  transition: all 0.2s ease;
}

.pricing-cta-button:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

/* Responsive layout */
@media (max-width: 768px) {
  .pricing-block {
    width: 100%;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .pricing-block {
    width: calc(50% - 1rem);
  }
}

@media (min-width: 1025px) {
  .pricing-block {
    width: calc(33.333% - 1rem);
  }
}
</style>
